import { useEffect, useState } from 'react';
import { TextField } from '@material-ui/core';
import { Autocomplete, Skeleton } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { useAdminGetOrganizations } from '../../../organizations/hooks/get-organizations.hook';
import { Organization, OrganizationDeliveryPoint } from '../../../organizations/types/organization.type';
import { stringDateToDeliveryDay } from '../../utils/format-delivery-schedule.util';
import { DeliveryAddress, DeliveryDay } from '../../types/delivery-selection.type';

type Props = {
  selectedAddress: DeliveryAddress | null;
  selectedDay: DeliveryDay | null;
  selectedDeliveryPointId: string | null;
  defaultOrganizationId?: string;
  onAddressChange: (address: DeliveryAddress | null) => void;
  onBillingAddressChange: (address: DeliveryAddress | null) => void;
  onDayChange: (day: DeliveryDay | null) => void;
  onDeliveryPointChange: (deliveryPointId: string | null) => void;
  onOrganizationChange: (organizationId: string | null) => void;
};

const useStyles = makeStyles({
  dateSchedule: {
    display: 'flex',
    flexDirection: 'row',
    gap: '0.5rem',
    alignItems: 'center',
  },
  noDeliveryPoints: {
    margin: 0,
    opacity: 0.7,
  },
});

function organizationToDeliveryAddress(organization: Organization): DeliveryAddress {
  return {
    type: 'store',
    _id: organization.id,
    address: organization.address.street,
    zipCode: organization.address.zipCode,
    city: organization.address.city,
    title: organization.name,
  };
}

function toScheduleTime(time: string): string {
  return time.replace(':', 'H');
}

function buildSchedule(start: string, end: string): string {
  return `${toScheduleTime(start)} - ${toScheduleTime(end)}`;
}

const DEFAULT_START = '09:00';
const DEFAULT_END = '17:30';

export function OrganizationDeliverySection({
  selectedAddress,
  selectedDay,
  selectedDeliveryPointId,
  defaultOrganizationId,
  onAddressChange,
  onBillingAddressChange,
  onDayChange,
  onDeliveryPointChange,
  onOrganizationChange,
}: Props) {
  const classes = useStyles();
  const { isLoading, organizations } = useAdminGetOrganizations({
    page: 1,
    limit: 100,
    sortDirection: 'asc',
    filters: {},
  });
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [startTime, setStartTime] = useState(DEFAULT_START);
  const [endTime, setEndTime] = useState(DEFAULT_END);

  useEffect(() => {
    if (!defaultOrganizationId || !organizations.length || selectedOrg) return;

    const matchingOrganization = organizations.find((organization) => organization.id === defaultOrganizationId);
    if (!matchingOrganization) return;

    setSelectedOrg(matchingOrganization);
    onOrganizationChange(matchingOrganization.id);
    const addr = organizationToDeliveryAddress(matchingOrganization);
    onAddressChange(addr);
    onBillingAddressChange(addr);
    onDayChange(stringDateToDeliveryDay(moment().format('YYYY-MM-DD'), buildSchedule(DEFAULT_START, DEFAULT_END)));
  }, [defaultOrganizationId, organizations]);

  const handleChangeOrganization = (organization: Organization | null) => {
    setSelectedOrg(organization);
    onDeliveryPointChange(null);
    onOrganizationChange(organization?.id ?? null);
    if (!organization) {
      onAddressChange(null);
      onBillingAddressChange(null);
      onDayChange(null);
      return;
    }
    const deliveryAddress = organizationToDeliveryAddress(organization);
    onAddressChange(deliveryAddress);
    onBillingAddressChange(deliveryAddress);
    onDayChange(stringDateToDeliveryDay(moment().format('YYYY-MM-DD'), buildSchedule(startTime, endTime)));
  };

  const handleDeliveryPointChange = (deliveryPoint: OrganizationDeliveryPoint | null) => {
    onDeliveryPointChange(deliveryPoint?.id ?? null);
  };

  const selectedDeliveryPoint =
    selectedOrg?.deliveryPoints.find((deliveryPoint) => deliveryPoint.id === selectedDeliveryPointId) ?? null;

  const dateValue = selectedDay?.date.format('YYYY-MM-DD') ?? moment().format('YYYY-MM-DD');

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onDayChange(stringDateToDeliveryDay(moment(event.target.value, 'YYYY-MM-DD'), buildSchedule(startTime, endTime)));
  };

  const handleStartTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStart = event.target.value;
    setStartTime(newStart);
    onDayChange(stringDateToDeliveryDay(dateValue, buildSchedule(newStart, endTime)));
  };

  const handleEndTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEnd = event.target.value;
    setEndTime(newEnd);
    onDayChange(stringDateToDeliveryDay(dateValue, buildSchedule(startTime, newEnd)));
  };

  if (isLoading)
    return (
      <>
        <Skeleton />
        <div />
      </>
    );

  return (
    <>
      <Autocomplete
        value={selectedOrg}
        onChange={(_, newVal) => handleChangeOrganization(typeof newVal === 'string' ? null : newVal)}
        options={[...organizations].sort((a, b) => a.name.localeCompare(b.name))}
        getOptionLabel={(opt) =>
          typeof opt === 'string' ? opt : `${opt.name} (${opt.address.city} - ${opt.address.zipCode})`
        }
        getOptionSelected={(opt, val) => opt.id === val.id}
        renderInput={(params) => <TextField {...params} label="Organisation" variant="outlined" size="small" />}
      />

      {selectedOrg && selectedOrg.deliveryPoints.length === 0 && (
        <p className={classes.noDeliveryPoints}>
          Cette organisation n&apos;a aucun point de livraison. Ajoutez-en depuis l&apos;onglet « Points de livraison »
          de sa fiche avant de créer une commande.
        </p>
      )}

      {selectedOrg && selectedOrg.deliveryPoints.length > 0 && (
        <Autocomplete
          value={selectedDeliveryPoint}
          onChange={(_, newVal) => handleDeliveryPointChange(typeof newVal === 'string' ? null : newVal)}
          options={[...selectedOrg.deliveryPoints].sort((a, b) => a.name.localeCompare(b.name))}
          getOptionLabel={(opt) =>
            typeof opt === 'string' ? opt : `${opt.name} (${opt.address.city} - ${opt.address.zipCode})`
          }
          getOptionSelected={(opt, val) => opt.id === val.id}
          renderInput={(params) => (
            <TextField {...params} label="Point de livraison" variant="outlined" size="small" required />
          )}
        />
      )}

      {selectedOrg ? (
        <div className={classes.dateSchedule}>
          <TextField
            label="Date de livraison"
            type="date"
            variant="outlined"
            size="small"
            value={dateValue}
            onChange={handleDateChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Entre"
            type="time"
            variant="outlined"
            size="small"
            value={startTime}
            onChange={handleStartTimeChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Et"
            type="time"
            variant="outlined"
            size="small"
            value={endTime}
            onChange={handleEndTimeChange}
            InputLabelProps={{ shrink: true }}
          />
        </div>
      ) : (
        <div />
      )}
    </>
  );
}
